import React, { useEffect, useMemo, useState } from "react";

/* ===================== helpers ===================== */
function parseMph(s) {
  const n = Number(String(s || "").replace("mph", "").trim());
  return Number.isFinite(n) ? n : 0;
}

function safeStr(x) {
  return x == null ? "" : String(x);
}

function parseForecastDateTime(s) {
  // "YYYY-MM-DD HH:mm:ss"
  return new Date(safeStr(s).replace(" ", "T"));
}

function roundDownToHour(d) {
  const x = new Date(d);
  x.setMinutes(0, 0, 0);
  return x;
}

function addHours(d, hrs) {
  const x = new Date(d);
  x.setHours(x.getHours() + hrs);
  return x;
}

function formatHourLabel(d) {
  const h = d.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12} ${ampm}`;
}

function formatHHMMTo12h(hhmmss) {
  // "20:05:00" -> "8:05 PM"
  const s = safeStr(hhmmss);
  const m = s.match(/^(\d{2}):(\d{2})/);
  if (!m) return s;
  let h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${min} ${ampm}`;
}

function conditionToIcon(cond) {
  const c = safeStr(cond).toLowerCase();
  if (c.includes("snow")) return "❄️";
  if (c.includes("rain") || c.includes("drizzle")) return "🌧️";
  if (c.includes("storm") || c.includes("thunder")) return "⛈️";
  if (c.includes("clear") || c.includes("sun")) return "☀️";
  if (c.includes("overcast") || c.includes("cloud")) return "☁️";
  if (c.includes("fog") || c.includes("mist")) return "🌫️";
  return "🌤️";
}

function calcImpact(windowRows) {
  if (!windowRows.length) return { delayRisk: 0, hitting: 0 };

  const avgTemp =
    windowRows.reduce((a, r) => a + Number(r["Temperature (°F)"] || 0), 0) /
    windowRows.length;

  const avgWind =
    windowRows.reduce((a, r) => a + parseMph(r["Wind Speed (mph)"]), 0) /
    windowRows.length;

  const anyRainSnow = windowRows.some((r) =>
    /rain|snow/i.test(safeStr(r.Conditions))
  );

  const delayRisk = Math.min(
    100,
    (anyRainSnow ? 55 : 10) +
      (avgWind >= 18 ? 12 : avgWind >= 12 ? 7 : 0) +
      (avgTemp <= 38 ? 10 : 0)
  );

  const outHours = windowRows.filter((r) => safeStr(r["Wind Effect"]) === "Out")
    .length;
  const inHours = windowRows.filter((r) => safeStr(r["Wind Effect"]) === "In")
    .length;
  const windBias = (outHours - inHours) / Math.max(1, windowRows.length);

  // positive = better for hitters
  const hitting = Math.round((avgTemp - 55) * 0.8 + windBias * avgWind * 1.25);

  return { delayRisk, hitting };
}

function normTeamAbbr(s) {
  return safeStr(s).trim().toUpperCase();
}

/**
 * Convert many possible time strings -> "HH:MM:SS" (24h).
 */
function normalizeTimeToHHMMSS(input) {
  const s = safeStr(input).trim();
  if (!s) return "";

  const timeMatch = s.match(/(\d{1,2}:\d{2})(:\d{2})?\s*([AP]M)?/i);
  if (!timeMatch) return "";

  let hhmm = timeMatch[1];
  let ss = timeMatch[2] ? timeMatch[2].slice(1) : "00";
  let ampm = timeMatch[3] ? timeMatch[3].toUpperCase() : "";

  // already 24h
  if (!ampm) {
    const parts = hhmm.split(":").map((x) => Number(x));
    if (parts.length < 2) return "";
    let h = parts[0];
    let m = parts[1];
    if (!Number.isFinite(h) || !Number.isFinite(m)) return "";
    h = Math.max(0, Math.min(23, h));
    m = Math.max(0, Math.min(59, m));
    return `${String(h).padStart(2, "0")}:${String(m).padStart(
      2,
      "0"
    )}:${String(ss).padStart(2, "0")}`;
  }

  const parts = hhmm.split(":").map((x) => Number(x));
  if (parts.length < 2) return "";
  let h = parts[0];
  const m = parts[1];

  if (ampm === "AM") {
    if (h === 12) h = 0;
  } else if (ampm === "PM") {
    if (h !== 12) h += 12;
  }

  return `${String(h).padStart(2, "0")}:${String(m).padStart(
    2,
    "0"
  )}:${String(ss).padStart(2, "0")}`;
}

/**
 * Accepts:
 * - array
 * - {games:[...]} / {data:[...]} / {rows:[...]}
 * - object keyed by id -> values are rows
 */
function coerceToArray(json) {
  if (!json) return [];
  if (Array.isArray(json)) return json;
  if (Array.isArray(json.games)) return json.games;
  if (Array.isArray(json.data)) return json.data;
  if (Array.isArray(json.rows)) return json.rows;
  if (typeof json === "object") return Object.values(json);
  return [];
}

/* ---------- score color helpers ---------- */
// Delay: higher is worse
function delayBadge(delayRisk) {
  if (delayRisk >= 70)
    return {
      text: `Delay ${delayRisk}/100`,
      cls: "bg-red-500/15 text-red-200 border-red-500/30",
    };
  if (delayRisk >= 45)
    return {
      text: `Delay ${delayRisk}/100`,
      cls: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    };
  if (delayRisk >= 25)
    return {
      text: `Delay ${delayRisk}/100`,
      cls: "bg-yellow-500/15 text-yellow-200 border-yellow-500/30",
    };
  return {
    text: `Delay ${delayRisk}/100`,
    cls: "bg-emerald-500/12 text-emerald-200 border-emerald-500/25",
  };
}

// Hitting: higher is better
function hittingBadge(hitting) {
  if (hitting >= 18)
    return {
      text: `Hitting +${hitting}`,
      cls: "bg-emerald-500/18 text-emerald-200 border-emerald-500/35",
    };
  if (hitting >= 8)
    return {
      text: `Hitting +${hitting}`,
      cls: "bg-lime-500/15 text-lime-200 border-lime-500/30",
    };
  if (hitting <= -18)
    return {
      text: `Hitting ${hitting}`,
      cls: "bg-sky-500/15 text-sky-200 border-sky-500/30",
    };
  if (hitting <= -8)
    return {
      text: `Hitting ${hitting}`,
      cls: "bg-blue-500/15 text-blue-200 border-blue-500/30",
    };
  return { text: `Hitting ${hitting}`, cls: "bg-white/6 text-white/80 border-white/12" };
}

function pickOU(g) {
  const v =
    g.OU ??
    g["O/U"] ??
    g["Over/Under"] ??
    g.overUnder ??
    g.over_under ??
    g.total ??
    g.Total ??
    g.game_total ??
    g.GameTotal;

  if (v == null) return "";
  const s = safeStr(v).trim();
  return s ? s : "";
}

/* ===================== wind helpers ===================== */
const DIR_TO_DEG = {
  N: 0,
  NNE: 22.5,
  NE: 45,
  ENE: 67.5,
  E: 90,
  ESE: 112.5,
  SE: 135,
  SSE: 157.5,
  S: 180,
  SSW: 202.5,
  SW: 225,
  WSW: 247.5,
  W: 270,
  WNW: 292.5,
  NW: 315,
  NNW: 337.5,
};

function normalizeDirectionLabel(dir) {
  return safeStr(dir).trim().toUpperCase().replace(/\s+/g, "");
}

function windDirectionToDegrees(dir, windEffect = "") {
  const d = normalizeDirectionLabel(dir);
  if (DIR_TO_DEG[d] != null) return DIR_TO_DEG[d];

  const raw = safeStr(dir).toLowerCase();
  if (raw.includes("north")) return 0;
  if (raw.includes("east")) return 90;
  if (raw.includes("south")) return 180;
  if (raw.includes("west")) return 270;

  const eff = safeStr(windEffect).toLowerCase();
  if (eff === "out") return 0;
  if (eff === "in") return 180;
  if (eff.includes("left")) return 270;
  if (eff.includes("right")) return 90;
  if (eff.includes("cross")) return 315;

  return 0;
}

/* ===================== Logos ===================== */
function TeamLogo({ abbr, size = 22 }) {
  const A = normTeamAbbr(abbr);
  const [srcIdx, setSrcIdx] = useState(0);

  const candidates = useMemo(() => {
    return [
      `/logos/mlb/${A}.png`, // YOUR folder
      `/logos/mlb/${A}.svg`,
      `/img/mlb/logos/${A}.png`,
      `/img/mlb/${A}.png`,
    ];
  }, [A]);

  const src = candidates[srcIdx] || candidates[candidates.length - 1];

  return (
    <div
      className="flex items-center justify-center rounded-full border border-white/10 bg-white/5 overflow-hidden"
      style={{ width: size + 10, height: size + 10 }}
      title={A}
    >
      <img
        src={src}
        alt={A}
        width={size}
        height={size}
        className="object-contain"
        onError={() => {
          if (srcIdx < candidates.length - 1) setSrcIdx(srcIdx + 1);
        }}
      />
    </div>
  );
}

/* ===================== wind arrow ===================== */
function WindArrow({
  direction = "",
  effect = "",
  mph = 0,
  size = 54,
  subtle = false,
}) {
  const deg = windDirectionToDegrees(direction, effect);
  const mphNum = Number.isFinite(Number(mph)) ? Number(mph) : 0;

  const pulseClass =
    mphNum >= 18
      ? "animate-[pulse_1.2s_ease-in-out_infinite]"
      : mphNum >= 10
      ? "animate-[pulse_1.8s_ease-in-out_infinite]"
      : "animate-[pulse_2.8s_ease-in-out_infinite]";

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
      title={`${safeStr(direction) || "Wind"} • ${Math.round(mphNum)} mph${
        effect ? ` • ${effect}` : ""
      }`}
    >
      <div className="absolute inset-0 rounded-full border border-white/10 bg-white/[0.04]" />
      <div className="absolute inset-[6px] rounded-full border border-white/8" />

      <div
        className={`${pulseClass} transition-transform duration-700 ease-out`}
        style={{ transform: `rotate(${deg}deg)` }}
      >
        <svg
          width={Math.round(size * (subtle ? 0.55 : 0.62))}
          height={Math.round(size * (subtle ? 0.55 : 0.62))}
          viewBox="0 0 64 64"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 32H42"
            stroke="rgba(255,255,255,0.92)"
            strokeWidth={subtle ? "5" : "6"}
            strokeLinecap="round"
          />
          <path
            d="M42 32L30 21"
            stroke="rgba(255,255,255,0.92)"
            strokeWidth={subtle ? "5" : "6"}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M42 32L30 43"
            stroke="rgba(255,255,255,0.92)"
            strokeWidth={subtle ? "5" : "6"}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}

/* ===================== component ===================== */
export default function MlbWeather() {
  const [weatherRows, setWeatherRows] = useState([]);
  const [pitcherRows, setPitcherRows] = useState([]);
  const [gameTimeRows, setGameTimeRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [sortMode, setSortMode] = useState("delay"); // "delay" | "hitting"

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);

        const weatherUrl = "/data/mlb/latest/weather_forecast_hourly.json";
        const pitchersUrl = "/data/mlb/latest/pitcher_data.json";
        const gameTimesUrl = "/data/mlb/latest/weather_game_time.json";

        const [wRes, pRes] = await Promise.all([
          fetch(weatherUrl, { cache: "no-store" }),
          fetch(pitchersUrl, { cache: "no-store" }),
        ]);

        const wJson = wRes.ok ? await wRes.json() : [];
        const pJson = pRes.ok ? await pRes.json() : [];

        // OPTIONAL game time file (no scary error if missing)
        let gtJson = [];
        try {
          const gtRes = await fetch(gameTimesUrl, { cache: "no-store" });
          if (gtRes.ok) gtJson = await gtRes.json();
        } catch {
          gtJson = [];
        }

        if (!mounted) return;

        setWeatherRows(coerceToArray(wJson));
        setPitcherRows(coerceToArray(pJson));
        setGameTimeRows(coerceToArray(gtJson));
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  /**
   * ✅ FIX: Pick slate date like this:
   * 1) If "today" exists in the weather file (America/New_York), use it
   * 2) Else, fall back to MOST COMMON date in the file
   */
  const slateDate = useMemo(() => {
    if (!weatherRows.length) return "";

    const counts = new Map();
    const dates = new Set();

    for (const r of weatherRows) {
      const d = safeStr(r["Forecast Time"]).slice(0, 10);
      if (!d) continue;
      dates.add(d);
      counts.set(d, (counts.get(d) || 0) + 1);
    }

    // Force "today" based on America/New_York (so it doesn't flip due to browser/system TZ)
    const todayStr = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date()); // "YYYY-MM-DD"

    if (dates.has(todayStr)) return todayStr;

    // fallback: most common
    let best = "";
    let bestN = 0;
    for (const [d, n] of counts.entries()) {
      if (n > bestN) {
        bestN = n;
        best = d;
      }
    }
    return best || "";
  }, [weatherRows]);

  // weather lookup by park/team abbreviation
  const weatherByAbbr = useMemo(() => {
    const m = new Map();
    weatherRows.forEach((r) => {
      const abbr = normTeamAbbr(r["Team Abbreviation"]);
      if (!abbr) return;
      if (!m.has(abbr)) m.set(abbr, []);
      m.get(abbr).push(r);
    });
    for (const [k, arr] of m.entries()) {
      arr.sort(
        (a, b) =>
          parseForecastDateTime(a["Forecast Time"]) -
          parseForecastDateTime(b["Forecast Time"])
      );
      m.set(k, arr);
    }
    return m;
  }, [weatherRows]);

  /**
   * Build games:
   * - Prefer weather_game_time.json when available
   * - Fallback to pitcher inference
   */
  const gamesRaw = useMemo(() => {
    const fromGameTimes = [];
    gameTimeRows.forEach((g) => {
      const home = normTeamAbbr(
        g.Home || g.home || g.HomeTeam || g.homeTeam || g.team
      );
      const away = normTeamAbbr(
        g.Away || g.away || g.AwayTeam || g.awayTeam || g.opp
      );
      const rawTime =
        g.Time || g.time || g.GameTime || g.gameTime || g.Start || g.start || "";
      const time = normalizeTimeToHHMMSS(rawTime);

      const matchup = safeStr(g.Matchup || g.matchup || "");
      let home2 = home;
      let away2 = away;
      if ((!away2 || !home2) && matchup.includes("@")) {
        const [a, h] = matchup.split("@").map((x) => normTeamAbbr(x));
        away2 = away2 || a;
        home2 = home2 || h;
      }

      const park = normTeamAbbr(
        g.Park ||
          g.park ||
          g.TeamAbbreviation ||
          g.teamAbbreviation ||
          g.HomeAbbr ||
          g.homeAbbr ||
          home2
      );

      const stadium = safeStr(g.Stadium || g.stadium || "");
      const ou = pickOU(g);

      if (home2 && away2 && time) {
        fromGameTimes.push({
          home: home2,
          away: away2,
          park: park || home2, // park aligns to HOME
          stadium,
          time,
          ou,
          _src: "gameTime",
        });
      }
    });

    if (fromGameTimes.length) return fromGameTimes;

    // fallback: infer from pitcher file
    const inferred = [];
    pitcherRows.forEach((p) => {
      const team = normTeamAbbr(p.Team || p.team);
      const opp = normTeamAbbr(p.Opp || p.opp);
      const park = normTeamAbbr(p.Park || p.park) || "";
      const time = normalizeTimeToHHMMSS(p.Time || p.time);

      if (!team || !opp || !time) return;

      // IMPORTANT: if park exists, that's HOME.
      // away is the other team.
      const home = park ? park : team;
      const away = home === team ? opp : team;

      inferred.push({
        home,
        away,
        park: home,
        stadium: safeStr(p.Park || p.park || ""),
        time,
        ou: "",
        _src: "pitcher",
      });
    });

    return inferred;
  }, [gameTimeRows, pitcherRows]);

  /**
   * Dedupe mirrored duplicates:
   * keep exactly ONE per {pair + time}
   */
  const games = useMemo(() => {
    const byPairTime = new Map();

    for (const g of gamesRaw) {
      const home = normTeamAbbr(g.home);
      const away = normTeamAbbr(g.away);
      const time = normalizeTimeToHHMMSS(g.time);
      const park = normTeamAbbr(g.park) || home;

      if (!home || !away || !time) continue;

      const pair = [home, away].sort().join("-");
      const key = `${pair}_${time}`;

      const score = (g._src === "gameTime" ? 100 : 0) + (park === home ? 10 : 0);

      const existing = byPairTime.get(key);
      if (!existing || score > existing._score) {
        byPairTime.set(key, { ...g, home, away, time, park, _score: score });
      }
    }

    return Array.from(byPairTime.values()).sort((a, b) => {
      if (a.time !== b.time) return a.time.localeCompare(b.time);
      return `${a.away}@${a.home}`.localeCompare(`${b.away}@${b.home}`);
    });
  }, [gamesRaw]);

  // Build cards (DO NOT filter out missing weather — still show the game)
  const gameCards = useMemo(() => {
    if (!slateDate) {
      return games.map((g) => ({
        g,
        startHour: null,
        end: null,
        windowRows: [],
        first: null,
        delayRisk: 0,
        hitting: 0,
      }));
    }

    return games.map((g) => {
      const park = normTeamAbbr(g.park) || normTeamAbbr(g.home);
      const stadiumRows = weatherByAbbr.get(park) || [];

      const start = new Date(`${slateDate}T${g.time}`);
      const startHour = roundDownToHour(start);
      const end = addHours(startHour, 3);

      let windowRows = stadiumRows.filter((r) => {
        const t = parseForecastDateTime(r["Forecast Time"]);
        return t >= startHour && t <= end;
      });

      // widen if needed
      if (!windowRows.length) {
        const start2 = addHours(startHour, -1);
        const end2 = addHours(end, 1);
        windowRows = stadiumRows.filter((r) => {
          const t = parseForecastDateTime(r["Forecast Time"]);
          return t >= start2 && t <= end2;
        });
      }

      const first = windowRows[0] || null;
      const impact = first ? calcImpact(windowRows) : { delayRisk: 0, hitting: 0 };

      return {
        g,
        startHour,
        end,
        windowRows,
        first,
        delayRisk: impact.delayRisk,
        hitting: impact.hitting,
      };
    });
  }, [games, slateDate, weatherByAbbr]);

  const sortedCards = useMemo(() => {
    const arr = [...gameCards];

    arr.sort((a, b) => {
      const aTime = a.g.time || "";
      const bTime = b.g.time || "";
      return aTime.localeCompare(bTime); // earliest first
    });

    return arr;
  }, [gameCards]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-zinc-950 text-white">
        <div className="text-white/70">Loading MLB Weather…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen !bg-zinc-950 !text-white opacity-100 brightness-100 contrast-100">
      <div className="mx-auto max-w-6xl px-4 py-8 opacity-100">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">MLB Weather</h1>
            <p className="text-white/60">
              Auto-slate: {slateDate || "—"} • Games: {sortedCards.length}
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setSortMode("delay")}
              className={`rounded-xl border px-3 py-2 text-sm hover:bg-white/10 ${
                sortMode === "delay"
                  ? "border-white/20 bg-white/10"
                  : "border-white/10 bg-white/5"
              }`}
            >
              Sort: Delay Risk
            </button>
            <button
              onClick={() => setSortMode("hitting")}
              className={`rounded-xl border px-3 py-2 text-sm hover:bg-white/10 ${
                sortMode === "hitting"
                  ? "border-white/20 bg-white/10"
                  : "border-white/10 bg-white/5"
              }`}
            >
              Sort: Hitting Boost
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="mt-6 grid gap-4">
          {sortedCards.map((card) => {
            const { g, startHour, end, windowRows, first, delayRisk, hitting } = card;

            const dBadge = delayBadge(delayRisk);
            const hBadge = hittingBadge(hitting);
            const ouText = safeStr(g.ou).trim();

            const parkKey = normTeamAbbr(g.park) || normTeamAbbr(g.home);

            // If no weather rows, we still render the header and show a message
            const temp0 = first ? Math.round(Number(first["Temperature (°F)"] || 0)) : null;
            const wind0 = first ? Math.round(parseMph(first["Wind Speed (mph)"])) : null;
            const windDir0 = first ? safeStr(first["Wind Direction"]) : "";
            const windEff0 = first ? safeStr(first["Wind Effect"]) : "";
            const hum0 = first ? Math.round(Number(first["Humidity (%)"] || 0)) : null;
            const conditions0 = first ? safeStr(first.Conditions) : "No forecast found for this park/time";

            return (
              <div
                key={`${g.away}@${g.home}_${g.time}`}
                className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent"
              >
                {/* Header */}
                <div className="flex flex-col gap-2 border-b border-white/10 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* logos */}
                    <div className="flex items-center gap-2">
                      <TeamLogo abbr={g.away} />
                      <span className="text-white/40">@</span>
                      <TeamLogo abbr={g.home} />
                    </div>

                    <div className="text-lg font-semibold">
                      {g.away} @ {g.home}
                    </div>

                    <span className={`rounded-full border px-2.5 py-1 text-xs ${dBadge.cls}`}>
                      {dBadge.text}
                    </span>
                    <span className={`rounded-full border px-2.5 py-1 text-xs ${hBadge.cls}`}>
                      {hBadge.text}
                    </span>

                    {ouText ? (
                      <span className="rounded-full border border-white/12 bg-white/6 px-2.5 py-1 text-xs text-white/85">
                        O/U {ouText}
                      </span>
                    ) : null}
                  </div>

                  <div className="text-sm text-white/60">
                    {slateDate || "—"} • {formatHHMMTo12h(g.time)} • {parkKey}
                  </div>
                </div>

                {/* Body */}
                <div className="grid gap-4 p-5 md:grid-cols-[260px_1fr]">
                  {/* Big tile */}
                  <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-3xl">{conditionToIcon(conditions0)}</div>
                      <div className="text-xs text-white/60">{conditions0}</div>
                    </div>

                    <div className="mt-4 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-5xl font-semibold">
                          {temp0 == null ? "—" : `${temp0}°`}
                        </div>
                      </div>

                      <div className="shrink-0 pr-1">
                        <WindArrow
                          direction={windDir0}
                          effect={windEff0}
                          mph={wind0}
                          size={62}
                        />
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                        <div className="text-white/60">Wind</div>
                        <div className="mt-1 font-medium">
                          {windDir0 ? `${windDir0} • ` : ""}
                          {wind0 == null ? "—" : `${wind0} mph`}
                        </div>
                        <div className="mt-1 text-xs text-white/60">{windEff0 || ""}</div>
                      </div>

                      <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                        <div className="text-white/60">Humidity</div>
                        <div className="mt-1 font-medium">{hum0 == null ? "—" : `${hum0}%`}</div>
                        <div className="mt-1 text-xs text-white/60">Carry factor</div>
                      </div>
                    </div>

                    <div className="mt-3 text-xs text-white/50">
                      Impact: <span className="text-white/70">Delay</span>{" "}
                      <span className="font-medium">{delayRisk}</span>/100 •{" "}
                      <span className="text-white/70">Hitting</span>{" "}
                      <span className="font-medium">
                        {hitting > 0 ? `+${hitting}` : hitting}
                      </span>
                    </div>

                    {!first ? (
                      <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-white/65">
                        No hourly forecast rows matched this game window for key{" "}
                        <span className="font-mono text-white/80">{parkKey}</span>.
                        <div className="mt-1 text-white/45">
                          (Game is still shown so you don’t lose slate count.)
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {/* Hourly strip */}
                  <div className="rounded-2xl border border-white/10 bg-black/10 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="text-sm font-medium">Game Window</div>
                      <div className="text-xs text-white/60">
                        {startHour && end
                          ? `${formatHourLabel(startHour)} → ${formatHourLabel(end)}`
                          : "—"}
                      </div>
                    </div>

                    {windowRows.length ? (
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                        {windowRows.map((r) => {
                          const t = parseForecastDateTime(r["Forecast Time"]);
                          const temp = Math.round(Number(r["Temperature (°F)"] || 0));
                          const wind = Math.round(parseMph(r["Wind Speed (mph)"]));
                          const windDir = safeStr(r["Wind Direction"]);
                          const windEff = safeStr(r["Wind Effect"]);
                          const cond = safeStr(r.Conditions);

                          return (
                            <div
                              key={safeStr(r["Forecast Time"]) + safeStr(r["Team Abbreviation"])}
                              className="rounded-xl border border-white/10 bg-white/5 p-3"
                            >
                              <div className="flex items-center justify-between">
                                <div className="text-xs text-white/60">{formatHourLabel(t)}</div>
                                <div className="text-lg">{conditionToIcon(cond)}</div>
                              </div>

                              <div className="mt-2 flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="text-lg font-semibold">{temp}°</div>
                                </div>

                                <div className="shrink-0 -mr-1">
                                  <WindArrow
                                    direction={windDir}
                                    effect={windEff}
                                    mph={wind}
                                    size={38}
                                    subtle
                                  />
                                </div>
                              </div>

                              <div className="mt-2 text-xs text-white/60">
                                Wind: {wind} mph • {windEff}
                              </div>

                              <div className="mt-1 text-[11px] text-white/50 line-clamp-1">{cond}</div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
                        No hourly rows for this window.
                      </div>
                    )}

                    <div className="mt-3 text-[11px] text-white/50">
                      Using key: <span className="font-mono">{parkKey}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {!sortedCards.length && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-white/70">
              No games found after parsing. If you expect games, verify{" "}
              <span className="font-mono text-white/80">pitcher_data.json</span> or{" "}
              <span className="font-mono text-white/80">weather_game_time.json</span>.
            </div>
          )}
        </div>

        <div className="mt-8 text-xs text-white/40">
          Logos expected at <span className="font-mono">/public/logos/mlb/TEAM.png</span>. Example:{" "}
          <span className="font-mono">/public/logos/mlb/ARI.png</span>.
        </div>
      </div>
    </div>
  );
}